(function () {
  'use strict';

  function initCarousel(root) {
    if (!root || root.dataset.carouselInit === '1') return;

    var viewport = root.querySelector('.o-reviews__viewport');
    var track = root.querySelector('.o-reviews__track');
    var prevBtn = root.querySelector('.o-reviews__nav--prev');
    var nextBtn = root.querySelector('.o-reviews__nav--next');
    if (!viewport || !track) return;

    var originals = Array.prototype.slice.call(track.children);
    var setCount = originals.length;
    if (setCount === 0) return;

    root.dataset.carouselInit = '1';

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ── Clone a full set before and after for infinite looping ──────────────
    function makeClone(node) {
      var c = node.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('tabindex', '-1');
      c.classList.add('is-clone');
      c.removeAttribute('id');
      // Clones are decorative (aria-hidden) — their inner Trustpilot link must
      // not be a real tab stop or a real click target.
      var cloneLink = c.querySelector('.o-reviews__card-link');
      if (cloneLink) {
        cloneLink.setAttribute('tabindex', '-1');
        cloneLink.removeAttribute('href');
      }
      return c;
    }
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    originals.forEach(function (n) { before.appendChild(makeClone(n)); });
    originals.forEach(function (n) { after.appendChild(makeClone(n)); });
    track.insertBefore(before, track.firstChild);
    track.appendChild(after);

    var cards = Array.prototype.slice.call(track.children); // 3 × setCount
    cards.forEach(function (c, i) { c.dataset.idx = String(i); });

    root.classList.add('is-ready');
    if (prevBtn) prevBtn.hidden = false;
    if (nextBtn) nextBtn.hidden = false;

    // ── State ───────────────────────────────────────────────────────────────
    var step = 0, card0Layout = 0, vpW = 0;
    var active = setCount;          // start on the first REAL card
    var translate = 0;
    var minScale = 0.74, shrink = 0.15, minOpacity = 0.32;
    var tweenRAF = null, dragRAF = null;

    // Measure fractional, scale-independent geometry. Card centres come from
    // getBoundingClientRect: because transform-origin is centred, the scale on
    // off-centre cards does NOT move their centre point, so this stays exact.
    // Using whole-pixel offsetWidth here instead would accumulate a sub-pixel
    // error across the clone-band offset and visibly shift the centre card.
    function measure() {
      var vpRect = viewport.getBoundingClientRect();
      vpW = vpRect.width;
      var r0 = cards[0].getBoundingClientRect();
      var r1 = cards[1].getBoundingClientRect();
      var c0 = (r0.left + r0.right) / 2 - vpRect.left;
      var c1 = (r1.left + r1.right) / 2 - vpRect.left;
      step = c1 - c0;
      card0Layout = c0 - translate; // track-local centre of card 0
    }

    function centeredTranslate(idx) {
      return (vpW / 2) - (card0Layout + idx * step);
    }

    function indexAt(tr) {
      return (vpW / 2 - card0Layout - tr) / step;
    }

    var centreIdx = -1;
    function applyTransform() {
      track.style.transform = 'translate3d(' + translate + 'px,0,0)';
      var center = vpW / 2;
      var nearest = 0, nearestD = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var cardCenter = translate + card0Layout + i * step;
        var d = Math.abs(cardCenter - center) / step;
        // Only the single centre card is full size; everything either side
        // shrinks + fades with distance from centre.
        var scale = Math.max(minScale, 1 - d * shrink);
        var opacity = Math.max(minOpacity, 1 - d * 0.30);
        cards[i].style.transform = 'scale(' + scale.toFixed(3) + ')';
        cards[i].style.opacity = opacity.toFixed(3);
        cards[i].style.zIndex = String(Math.max(0, Math.round(100 - d * 10)));
        if (d < nearestD) { nearestD = d; nearest = i; }
      }
      // Flag the centred card so CSS can animate its stars. Only update on a
      // real change so the animation replays per card, not every frame.
      if (nearest !== centreIdx) {
        if (centreIdx > -1 && cards[centreIdx]) cards[centreIdx].classList.remove('is-centre');
        cards[nearest].classList.add('is-centre');
        centreIdx = nearest;
      }
    }

    // Keep the active card inside the middle clone band so there is always
    // content either side. The three sets are identical, so a ±setWidth jump
    // is visually seamless.
    function normalize() {
      var setW = setCount * step;
      if (active < setCount) { active += setCount; translate -= setW; }
      else if (active >= 2 * setCount) { active -= setCount; translate += setW; }
    }

    function tweenTo(idx, dur) {
      if (tweenRAF) cancelAnimationFrame(tweenRAF);
      var from = translate;
      var to = centeredTranslate(idx);
      var start = null;
      dur = dur || 460;
      function frame(now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / dur);
        var e = 1 - Math.pow(1 - t, 3); // easeOutCubic
        translate = from + (to - from) * e;
        applyTransform();
        if (t < 1) {
          tweenRAF = requestAnimationFrame(frame);
        } else {
          active = idx;
          normalize();
          translate = centeredTranslate(active);
          applyTransform();
          tweenRAF = null;
        }
      }
      tweenRAF = requestAnimationFrame(frame);
    }

    function goTo(idx, dur) { tweenTo(idx, dur || 460); }
    function next() { goTo(active + 1); }
    function prev() { goTo(active - 1); }

    function layout(recenter) {
      measure();
      if (recenter) translate = centeredTranslate(active);
      applyTransform();
    }

    // ── Buttons ──────────────────────────────────────────────────────────────
    if (nextBtn) nextBtn.addEventListener('click', function () { next(); restartAuto(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); restartAuto(); });

    // ── Drag / swipe (with flick momentum) ───────────────────────────────────
    var dragging = false, startX = 0, startTranslate = 0, moved = 0, pId = null;
    var lastX = 0, velocity = 0, lastT = 0;
    // Set on a real drag (moved >= 6) so the browser's synthesized click that
    // follows pointerup can be cancelled — otherwise a swipe that ends over
    // the card-link anchor would also navigate to Trustpilot.
    var wasDrag = false;
    var PROJECT_MS = 190;  // how far a flick "throws" the track
    var MAX_FLICK = 6;     // cap cards advanced per flick
    function onDown(e) {
      if (e.button != null && e.button !== 0) return;
      dragging = true; moved = 0; velocity = 0;
      startX = e.clientX; lastX = e.clientX; lastT = performance.now();
      startTranslate = translate; pId = e.pointerId;
      if (tweenRAF) { cancelAnimationFrame(tweenRAF); tweenRAF = null; }
      viewport.classList.add('is-dragging');
      try { viewport.setPointerCapture(pId); } catch (err) {}
      stopAuto();
    }
    function onMove(e) {
      if (!dragging) return;
      var now = performance.now();
      var dt = now - lastT;
      if (dt > 0) {
        var inst = (e.clientX - lastX) / dt;        // px / ms
        velocity = velocity * 0.7 + inst * 0.3;     // smooth out jitter
      }
      lastX = e.clientX; lastT = now;
      moved = Math.abs(lastX - startX);
      if (!dragRAF) {
        dragRAF = requestAnimationFrame(function () {
          dragRAF = null;
          translate = startTranslate + (lastX - startX);
          applyTransform();
        });
      }
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      try { viewport.releasePointerCapture(pId); } catch (err) {}
      if (dragRAF) { cancelAnimationFrame(dragRAF); dragRAF = null; }
      if (moved < 6) {
        // Genuine click, not a drag — let the card-link anchor's native
        // navigation proceed. Also bring the clicked card to centre.
        wasDrag = false;
        var card = e.target && e.target.closest ? e.target.closest('.o-reviews__card') : null;
        if (card && card.dataset.idx != null && parseInt(card.dataset.idx, 10) !== active) {
          goTo(parseInt(card.dataset.idx, 10));
        } else {
          layout(true);
        }
      } else {
        // Was a drag — the click event that follows pointerup must not
        // trigger the card-link anchor's navigation.
        wasDrag = true;
        // Project the throw: fast flicks travel several cards, slow drags snap
        // to the nearest. Clamp so a hard flick can't overshoot the clone band.
        var projected = translate + velocity * PROJECT_MS;
        var target = Math.round(indexAt(projected));
        target = Math.max(active - MAX_FLICK, Math.min(active + MAX_FLICK, target));
        var dist = Math.abs(target - active);
        goTo(target, Math.min(700, 340 + dist * 70));
      }
      restartAuto();
    }
    // Capture phase, so this runs before the anchor's own default navigation.
    viewport.addEventListener('click', function (e) {
      if (wasDrag) {
        e.preventDefault();
        e.stopPropagation();
        wasDrag = false;
      }
    }, true);
    viewport.addEventListener('pointerdown', onDown);
    viewport.addEventListener('pointermove', onMove);
    viewport.addEventListener('pointerup', onUp);
    viewport.addEventListener('pointercancel', onUp);
    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // ── Keyboard ─────────────────────────────────────────────────────────────
    viewport.setAttribute('tabindex', '0');
    viewport.setAttribute('role', 'group');
    viewport.setAttribute('aria-label', 'Customer reviews carousel — use arrow keys or drag');
    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); restartAuto(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); restartAuto(); }
    });

    // ── Autoplay ─────────────────────────────────────────────────────────────
    var autoTimer = null;
    var AUTO_MS = 4800;
    function startAuto() {
      if (reduceMotion || autoTimer) return;
      autoTimer = setInterval(next, AUTO_MS);
    }
    function stopAuto() { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } }
    function restartAuto() { stopAuto(); startAuto(); }

    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    root.addEventListener('focusin', stopAuto);
    root.addEventListener('focusout', startAuto);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAuto(); else startAuto();
    });

    // ── Resize ───────────────────────────────────────────────────────────────
    var rT = null;
    window.addEventListener('resize', function () {
      clearTimeout(rT);
      rT = setTimeout(function () { layout(true); }, 150);
    });

    // ── Init ─────────────────────────────────────────────────────────────────
    layout(true);
    window.addEventListener('load', function () { layout(true); });
    startAuto();
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-reviews-carousel]');
    for (var i = 0; i < nodes.length; i++) initCarousel(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('[data-reviews-carousel]') : null;
    if (el) initCarousel(el);
  });
})();
